import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react"

interface AssetItem {
  id: string
  name: string
}

interface CustomerItem {
  id: string
  name: string
  sites: Array<{ id: string; name: string }>
}

interface SiteItem {
  id: string
  name: string
}

export interface OrderWorkflowState {
  // Order form data
  name: string | undefined
  start: string
  end: string
  selectedAssets: AssetItem[] // Changed from single asset to array
  customer?: CustomerItem
  site?: SiteItem
  quantity?: number
  pendingCustomerName?: string // Captures typed customer name before creation

  // Calendar-specific state
  calendarOpen: boolean
  calendarBuffer: number
  calendarLoading: boolean
  calendarRefreshing: boolean
  currentOrderId?: string // ID of order being edited (deprecated, use currentFulfillmentId)
  multipleAssets?: boolean // Whether to allow multiple assets to be selected
}

interface OrderWorkflowActions {
  updateOrderName: (name: string) => void
  updateOrderDates: (start: string, end: string) => void
  updateOrderQuantity: (quantity: number | undefined) => void
  addAsset: (asset: AssetItem) => void
  removeAsset: (assetId: string) => void
  clearAssets: () => void
  selectCustomer: (customer: CustomerItem | undefined) => void
  selectSite: (site: SiteItem | undefined) => void
  setPendingCustomerName: (name: string) => void
  openCalendar: (
    start: string,
    end: string,
    selectedAssets?: AssetItem[],
    currentOrderId?: string,
    multipleAssets?: boolean
  ) => void
  setCalendarLoading: (loading: boolean) => void
  setCalendarRefreshing: (refreshing: boolean) => void
  updateFormField: <K extends keyof OrderWorkflowState>(field: K, value: OrderWorkflowState[K]) => void
  closeCalendar: () => void
  updateCalendarBuffer: (buffer: number) => void
  resetWorkflow: () => void
}

// Split into two contexts to prevent unnecessary re-renders
// Components that only need actions won't re-render when state changes
const OrderWorkflowStateContext = createContext<OrderWorkflowState | undefined>(undefined)
const OrderWorkflowActionsContext = createContext<OrderWorkflowActions | undefined>(undefined)

const initialState: OrderWorkflowState = {
  name: undefined,
  start: "",
  end: "",
  quantity: undefined,
  selectedAssets: [],
  customer: undefined,
  site: undefined,
  pendingCustomerName: undefined,
  calendarOpen: false,
  calendarBuffer: 4,
  calendarLoading: false,
  calendarRefreshing: false,
  currentOrderId: undefined,
  multipleAssets: false,
}

export const OrderWorkflowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OrderWorkflowState>(initialState)

  // Update order dates (from calendar or form)
  const updateOrderName = useCallback((name: string) => {
    setState((prev) => ({
      ...prev,
      name,
    }))
  }, []) // Truly stable - no dependencies

  const updateOrderDates = useCallback((start: string, end: string) => {
    setState((prev) => ({
      ...prev,
      start,
      end,
    }))
  }, []) // Truly stable - no dependencies

  const updateOrderQuantity = useCallback((quantity: number | undefined) => {
    setState((prev) => ({
      ...prev,
      quantity,
    }))
  }, []) // Truly stable - no dependencies

  // Add asset to selection
  const addAsset = useCallback((asset: AssetItem) => {
    setState((prev) => {
      // Don't add if already exists
      if (prev.selectedAssets.some((a) => a.id === asset.id)) {
        return prev
      }
      return {
        ...prev,
        selectedAssets: [...prev.selectedAssets, asset],
        calendarOpen: false, // Close calendar after selection
      }
    })
  }, []) // Truly stable - no dependencies

  // Remove asset from selection
  const removeAsset = useCallback(
    (assetId: string) => {
      setState((prev) => ({
        ...prev,
        selectedAssets: prev.selectedAssets.filter((a) => a.id !== assetId),
      }))
    },
    [] // Truly stable - no dependencies
  )

  // Clear all assets
  const clearAssets = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedAssets: [],
    }))
  }, [])

  // Select customer
  const selectCustomer = useCallback(
    (customer: CustomerItem | undefined) => {
      setState((prev) => ({
        ...prev,
        customer,
      }))
    },
    [] // Truly stable - no dependencies
  )

  // Select site
  const selectSite = useCallback(
    (site: SiteItem | undefined) => {
      setState((prev) => ({
        ...prev,
        site,
      }))
    },
    [] // Truly stable - no dependencies
  )

  // Set pending customer name (for creation flow)
  const setPendingCustomerName = useCallback((name: string) => {
    setState((prev) => ({
      ...prev,
      pendingCustomerName: name,
    }))
  }, [])

  // Open calendar with current dates
  const openCalendar = useCallback(
    (start: string, end: string, selectedAssets?: AssetItem[], currentOrderId?: string, multipleAssets?: boolean) => {
      setState((prev) => ({
        ...prev,
        start,
        end,
        selectedAssets: selectedAssets || prev.selectedAssets,
        currentOrderId,
        multipleAssets,
        calendarOpen: true,
      }))
    },
    []
  ) // Truly stable - no dependencies

  // Set calendar loading state (initial load)
  const setCalendarLoading = useCallback((loading: boolean) => {
    setState((prev) => ({
      ...prev,
      calendarLoading: loading,
    }))
  }, []) // Truly stable - no dependencies

  // Set calendar refreshing state (refetch with existing data)
  const setCalendarRefreshing = useCallback((refreshing: boolean) => {
    setState((prev) => ({
      ...prev,
      calendarRefreshing: refreshing,
    }))
  }, []) // Truly stable - no dependencies

  // Generic field update
  const updateFormField = useCallback(<K extends keyof OrderWorkflowState>(field: K, value: OrderWorkflowState[K]) => {
    setState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }, []) // Truly stable - no dependencies

  // Close calendar
  const closeCalendar = useCallback(() => {
    setState((prev) => ({
      ...prev,
      calendarOpen: false,
    }))
  }, [])

  // Update calendar buffer
  const updateCalendarBuffer = useCallback((buffer: number) => {
    setState((prev) => ({
      ...prev,
      calendarBuffer: buffer,
    }))
  }, [])

  // Reset workflow to initial state
  const resetWorkflow = useCallback(() => {
    setState(initialState)
  }, [])

  // Memoize actions - these never change since callbacks are truly stable
  const actions: OrderWorkflowActions = useMemo(
    () => ({
      updateOrderName,
      updateOrderDates,
      updateOrderQuantity,
      addAsset,
      removeAsset,
      clearAssets,
      selectCustomer,
      selectSite,
      setPendingCustomerName,
      openCalendar,
      closeCalendar,
      updateCalendarBuffer,
      setCalendarLoading,
      setCalendarRefreshing,
      resetWorkflow,
      updateFormField,
    }),
    [] // Empty array - callbacks are stable with no dependencies
  )

  return (
    <OrderWorkflowStateContext.Provider value={state}>
      <OrderWorkflowActionsContext.Provider value={actions}>{children}</OrderWorkflowActionsContext.Provider>
    </OrderWorkflowStateContext.Provider>
  )
}

// Hook to access state only (will re-render when state changes)
export const useOrderWorkflowState = () => {
  const context = useContext(OrderWorkflowStateContext)
  if (context === undefined) {
    throw new Error("useOrderWorkflowState must be used within an OrderWorkflowProvider")
  }
  return context
}

// Hook to access actions only (will NOT re-render when state changes)
export const useOrderWorkflowActions = () => {
  const context = useContext(OrderWorkflowActionsContext)
  if (context === undefined) {
    throw new Error("useOrderWorkflowActions must be used within an OrderWorkflowProvider")
  }
  return context
}

// Convenience hook to access both (for backward compatibility)
// Use sparingly - prefer useOrderWorkflowState or useOrderWorkflowActions
export const useOrderWorkflow = () => {
  const state = useOrderWorkflowState()
  const actions = useOrderWorkflowActions()
  return { state, ...actions }
}

// Selector hooks for specific parts of state (prevents unnecessary re-renders)
export const useOrderDates = () => {
  const state = useOrderWorkflowState()
  return { start: state.start, end: state.end }
}

export const useSelectedAssets = () => {
  const state = useOrderWorkflowState()
  return state.selectedAssets
}

export const useMultipleAssets = () => {
  const state = useOrderWorkflowState()
  return state.multipleAssets
}

export const useCalendarState = () => {
  const state = useOrderWorkflowState()
  return {
    open: state.calendarOpen,
    start: state.start,
    end: state.end,
    selectedAssets: state.selectedAssets,
    buffer: state.calendarBuffer,
  }
}

export const useCustomer = () => {
  const state = useOrderWorkflowState()
  return state.customer
}

export const useSite = () => {
  const state = useOrderWorkflowState()
  return state.site
}
